import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { apiFetch, setToken as apiSetToken, clearToken as apiClearToken, getToken, fetchMe } from '../api'

const AuthContext = createContext(undefined) // 👈 important: undefined, pas null

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>. Place AuthProvider above this component tree.')
  }
  return ctx
}

// Optionnel: hook "safe" si tu veux parfois tolérer l’absence du provider
export function useAuthOptional() {
  return useContext(AuthContext) || null
}

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => getToken() || '')
  const [user, setUser] = useState(null)
  const [booting, setBooting] = useState(true)

  useEffect(() => {
    let mounted = true

    async function boot() {
      try {
        const me = await fetchMe()
        if (!mounted) return
        setTokenState(getToken() || '')
        setUser(me || null)
      } catch (e) {
        apiClearToken()
        if (!mounted) return
        setTokenState('')
        setUser(null)
      } finally {
        if (mounted) setBooting(false)
      }
    }

    boot()
    return () => { mounted = false }
  }, [])

  const logout = useCallback(() => {
    apiClearToken()
    setTokenState('')
    setUser(null)
  }, [])

  const login = useCallback(async (username, password) => {
    const data = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: { username, password },
    })

    if (data?.token) {
      apiSetToken(data.token)
      setTokenState(data.token)
    } else {
      apiClearToken()
      setTokenState('')
    }

    const u = data?.user ? data.user : await fetchMe()
    setUser(u || null)

    return { token: data?.token || '', user: u }
  }, [])

  const recheck = useCallback(async (password) => {
    const resp = await apiFetch('/api/auth/recheck', {
      method: 'POST',
      body: { password },
    })
    return resp?.ok === true
  }, [])

  const hasRole = useCallback((requiredRoles = []) => {
    const roles = Array.isArray(user?.roles) ? user.roles : []
    return requiredRoles.some(r => roles.includes(r))
  }, [user])

  const value = useMemo(() => ({
    user,
    token,
    booting,
    isLoggedIn: !!user,
    login,
    logout,
    recheck,
    hasRole,
    setUser,
  }), [user, token, booting, login, logout, recheck, hasRole])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
